import { cn } from '@/lib/cn';
import { useDashboard } from '@/hooks/useApi';
import { useTranslation } from 'react-i18next';
import {
  CurrencyEuroIcon,
  DocumentTextIcon,
  ClockIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

export default function StatsCard() {
  const { t } = useTranslation();
  const { data, isLoading } = useDashboard();

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 rounded-lg border border-gray-200 bg-gray-50 animate-pulse" />
        ))}
      </div>
    );
  }

  const revenue = parseFloat(data?.revenue?.gross || '0');
  const outstanding = parseFloat(data?.outstandingInvoices?.total || '0');
  const outstandingCount = data?.outstandingInvoices?.count || 0;
  const totalInvoices = (data?.invoiceStatusBreakdown || []).reduce((sum, s) => sum + s.count, 0);
  const topClientsCount = (data?.topClients || []).length;
  const isEmpty = revenue === 0 && totalInvoices === 0 && topClientsCount === 0;

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 mb-3">
          <CurrencyEuroIcon className="h-6 w-6 text-indigo-500" />
        </div>
        <p className="text-sm font-medium text-gray-900">{t('dashboard.emptyStats')}</p>
        <p className="text-xs text-gray-500 mt-1">{t('dashboard.emptyStatsHint')}</p>
      </div>
    );
  }

  const stats = [
    { label: t('dashboard.revenue'), value: `€${revenue.toLocaleString('en', { minimumFractionDigits: 0 })}`, icon: CurrencyEuroIcon, color: 'text-emerald-600 bg-emerald-50' },
    { label: t('dashboard.invoices'), value: String(totalInvoices), icon: DocumentTextIcon, color: 'text-blue-600 bg-blue-50' },
    { label: t('dashboard.outstanding'), value: `€${outstanding.toLocaleString('en', { minimumFractionDigits: 0 })} (${outstandingCount})`, icon: ClockIcon, color: 'text-amber-600 bg-amber-50' },
    { label: t('dashboard.clients'), value: String(topClientsCount), icon: UserGroupIcon, color: 'text-violet-600 bg-violet-50' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className={cn(
            'flex flex-col gap-1 rounded-lg border border-gray-200 bg-gray-50 p-3.5',
            'transition-all duration-200 ease-in-out hover:border-gray-300 hover:shadow-sm',
          )}
        >
          <div className="flex items-center gap-2">
            <div className={cn('flex h-6 w-6 items-center justify-center rounded-md', stat.color)}>
              <stat.icon className="h-3.5 w-3.5" />
            </div>
            <span className="text-xs text-gray-500 font-medium">{stat.label}</span>
          </div>
          <span className="text-lg font-semibold text-gray-900 tracking-tight">{stat.value}</span>
        </div>
      ))}
    </div>
  );
}
